import { Button, formatDateTime, ListView, PageHeader, TextButton, useCursorList } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { integrationsQuery } from "../../connections";
import { placesQuery, tanksQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { RouterLink } from "../../router-link";
import { useDebounced } from "../../search";
import {
  isLow,
  levelText,
  ownerHref,
  SENSOR_STATUS,
  type Sensor,
  type SensorStatus,
  sensorActions,
} from "../../sensors";
import { ReadingsDialog } from "../customers/places";
import { type Payer, PayerPicker } from "../invoices/payer";
import styles from "../settings/settings.module.css";

const STATUSES = Object.keys(SENSOR_STATUS) as SensorStatus[];
// tank: "on" a tank, or "off" every tank (to place, e.g. after the migration)
export const sensorFilters = filterSearch({
  q: "text",
  show: ["all", "low"] as const,
  status: STATUSES,
  tank: ["on", "off"] as const,
});
type SensorSearch = ReturnType<typeof sensorFilters.validate>;

/** What the list asks the API for: Low is the API's own `low=true` (tanks in a low-level episode). */
export const sensorListQuery = (search: SensorSearch) => ({
  low: search.show === "low" ? ("true" as const) : undefined,
  q: search.q,
  status: search.status,
  on_tank: search.tank === undefined ? undefined : search.tank === "on" ? ("true" as const) : ("false" as const),
});

/** Tank sensors (P8-25): every device, its tank's level, and the tanks running low. */
export function SensorsPage(): ReactNode {
  const search = useSearch({ from: "/console/sensors" });
  const navigate = useNavigate({ from: "/sensors" });
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const show = search.show ?? "all";
  const [typed, setTyped] = useState(search.q ?? "");
  const q = useDebounced(typed.trim());
  useEffect(() => {
    if ((search.q ?? "") !== q) void navigate({ search: (s) => ({ ...s, q: q || undefined }), replace: true });
  }, [q, search.q, navigate]);
  const filtered = sensorFilters.active(search).some(([key]) => key !== "show");
  const [dialog, setDialog] = useState<{ kind: "register" } | { kind: "edit" | "readings"; sensor: Sensor } | null>(
    null,
  );
  const list = useCursorList({
    queryKey: ["sensors", "list", show, search.q ?? "", search.status ?? "", search.tank ?? ""],
    fetchPage: (cursor) =>
      ok(api.GET("/v1/admin/sensors", { params: { query: { ...sensorListQuery(search), cursor, limit: 25 } } })),
  });
  // the sensor account by name, when there's more than one to tell apart
  const integrations = useQuery(integrationsQuery);
  const accounts = (integrations.data ?? []).filter((i) => i.kind === "sensor");
  const accountOf = (id: string) => (accounts.length > 1 ? accounts.find((i) => i.id === id)?.name : undefined);
  const poll = useMutation({
    mutationFn: sensorActions.poll,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sensors"] }),
  });
  return (
    <>
      <PageHeader
        title="Tank sensors"
        description="Tank level sensors, and the tanks running low."
        actions={
          <div className={styles.actions}>
            <Button variant="secondary" loading={poll.isPending} onClick={() => poll.mutate()}>
              Read now
            </Button>
            <Button onClick={() => setDialog({ kind: "register" })}>Register a sensor</Button>
          </div>
        }
      />
      {poll.data ? (
        <Alert tone="success" title="Sensors read">
          {poll.data.stored} new {poll.data.stored === 1 ? "reading" : "readings"}
          {poll.data.alerts
            ? `, ${poll.data.alerts} low-level ${poll.data.alerts === 1 ? "alert" : "alerts"} sent`
            : ""}
          .
        </Alert>
      ) : null}
      {poll.error ? (
        <Alert tone="danger" title="Not read">
          {messageFor(errorOf(poll.error))}
        </Alert>
      ) : null}
      <ListView
        caption="Tank sensors"
        itemName={{ one: "sensor", other: "sensors" }}
        rows={list.rows}
        rowKey={(s) => s.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={filtered}
        onResetFilters={() => {
          setTyped("");
          void navigate({ search: (s) => ({ show: s.show }) });
        }}
        empty={
          show === "low"
            ? {
                title: "No tank is low",
                description: "Tanks show here from their first reading under their low level.",
              }
            : { title: "No sensors yet", description: "Register a device with Register a sensor." }
        }
        toolbar={
          <div className={styles.toolbar}>
            <div className={styles.searchBox}>
              <SearchField
                label="Search sensors"
                value={typed}
                onValueChange={setTyped}
                placeholder="Device ID, name, tank, place or owner"
              />
            </div>
            <SegmentedControl
              label="Show"
              value={show}
              onValueChange={(v) => navigate({ search: (s) => ({ ...s, show: v === "all" ? undefined : "low" }) })}
              options={[
                { value: "all", label: "All" },
                { value: "low", label: "Low" },
              ]}
            />
            <Select
              label="Status"
              value={search.status ?? "any"}
              onValueChange={(v) =>
                navigate({ search: (s) => ({ ...s, status: v === "any" ? undefined : (v as SensorStatus) }) })
              }
              options={[
                { value: "any", label: "Any status" },
                ...STATUSES.map((s) => ({ value: s, label: SENSOR_STATUS[s][0] })),
              ]}
            />
            <Select
              label="Tank"
              value={search.tank ?? "any"}
              onValueChange={(v) =>
                navigate({ search: (s) => ({ ...s, tank: v === "on" || v === "off" ? v : undefined }) })
              }
              options={[
                { value: "any", label: "On a tank or not" },
                { value: "on", label: "On a tank" },
                { value: "off", label: "Not on a tank" },
              ]}
            />
          </div>
        }
        columns={[
          {
            key: "sensor",
            label: "Sensor",
            render: (s) => (
              <span className={styles.stack}>
                <span>{s.label ?? s.external_id}</span>
                <span className={styles.muted}>
                  {["Device " + s.external_id, accountOf(s.integration_id)].filter(Boolean).join(" · ")}
                </span>
              </span>
            ),
          },
          {
            key: "tank",
            label: "Tank",
            render: (s) => {
              if (!s.tank) return <span className={styles.muted}>Not on a tank</span>;
              const href = ownerHref(s.tank.owner);
              const where = [s.tank.label ?? "Tank", s.tank.location?.label].filter(Boolean).join(" · ");
              return (
                <span className={styles.stack}>
                  <span>{where}</span>
                  {s.tank.owner && href ? (
                    <RouterLink href={href} className={styles.link}>
                      {s.tank.owner.name}
                    </RouterLink>
                  ) : null}
                </span>
              );
            },
          },
          {
            key: "level",
            label: "Level",
            render: (s) => (
              <span className={styles.stack}>
                <span className={isLow(s) ? styles.danger : undefined}>{levelText(s)}</span>
                {s.low_alert_at ? (
                  <span className={styles.danger}>Low since {formatDateTime(s.low_alert_at, format)}</span>
                ) : s.tank ? (
                  <span className={styles.muted}>Low at {s.tank.low_level_percent}%</span>
                ) : null}
              </span>
            ),
          },
          {
            key: "read",
            label: "Read",
            render: (s) => (
              <span className={styles.stack}>
                <span className={styles.muted}>{s.latest ? formatDateTime(s.latest.measured_at, format) : "—"}</span>
                <span className={styles.muted}>
                  {s.last_polled_at ? `Checked ${formatDateTime(s.last_polled_at, format)}` : "Not checked yet"}
                </span>
              </span>
            ),
          },
          {
            key: "status",
            label: "Status",
            render: (s) => {
              const [label, tone] = SENSOR_STATUS[s.status];
              return (
                <span className={styles.actions}>
                  <Badge tone={tone}>{label}</Badge>
                  {isLow(s) ? <Badge tone="danger">Low</Badge> : null}
                </span>
              );
            },
          },
          {
            key: "actions",
            label: "",
            render: (s) => (
              <span className={styles.actions}>
                {s.latest ? (
                  <TextButton onClick={() => setDialog({ kind: "readings", sensor: s })}>Readings</TextButton>
                ) : null}
                <TextButton onClick={() => setDialog({ kind: "edit", sensor: s })}>Edit</TextButton>
              </span>
            ),
          },
        ]}
      />
      {dialog?.kind === "register" ? <SensorDialog onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "edit" ? <SensorDialog sensor={dialog.sensor} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === "readings" ? (
        <ReadingsDialog
          sensorId={dialog.sensor.id}
          title={dialog.sensor.label ?? dialog.sensor.external_id}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/** Whose place, which place, which tank. */
function TankChooser({ value, onChange }: { value: string | null; onChange: (tankId: string | null) => void }) {
  const [owner, setOwner] = useState<Payer | null>(null);
  const [placeId, setPlaceId] = useState<string>();
  const places = useQuery({ ...placesQuery(owner?.billee ?? { customer_id: "" }), enabled: owner !== null });
  const tanks = useQuery({ ...tanksQuery(placeId ?? ""), enabled: Boolean(placeId) });
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Tank</legend>
      <PayerPicker
        label="Whose tank"
        value={owner}
        onChange={(o) => {
          setOwner(o);
          setPlaceId(undefined);
          onChange(null);
        }}
      />
      {owner ? (
        <Select
          label="Place"
          placeholder={places.data?.length === 0 ? "No places" : "Choose a place"}
          value={placeId}
          onValueChange={(v) => {
            setPlaceId(v);
            onChange(null);
          }}
          options={(places.data ?? []).map((p) => ({ value: p.id, label: p.label }))}
        />
      ) : null}
      {placeId ? (
        <Select
          label="Tank"
          placeholder={tanks.data?.length === 0 ? "No tanks: add one on the customer's page" : "Choose a tank"}
          value={value ?? undefined}
          onValueChange={onChange}
          options={(tanks.data ?? []).map((t) => ({
            value: t.id,
            label: [t.label ?? "Tank", t.sensor_id ? "(has a sensor)" : t.is_active ? null : "(retired)"]
              .filter(Boolean)
              .join(" "),
            disabled: t.sensor_id !== null || !t.is_active,
          }))}
        />
      ) : null}
    </fieldset>
  );
}

/** Register a device (its MobiWater id) or change one: its id, name, whether it's read, its tank. */
function SensorDialog({ sensor, onClose }: { sensor?: Sensor; onClose: () => void }) {
  const queryClient = useQueryClient();
  const integrations = useQuery(integrationsQuery);
  const sensorIntegrations = (integrations.data ?? []).filter((i) => i.kind === "sensor");
  const [integrationId, setIntegrationId] = useState<string>();
  const [externalId, setExternalId] = useState(sensor?.external_id ?? "");
  const [label, setLabel] = useState(sensor?.label ?? "");
  const [status, setStatus] = useState<SensorStatus>(sensor?.status ?? "active");
  const [moving, setMoving] = useState(!sensor);
  const [tankId, setTankId] = useState<string | null>(null);
  const chosenIntegration = integrationId ?? (sensorIntegrations.length === 1 ? sensorIntegrations[0].id : undefined);
  const save = useMutation({
    mutationFn: () =>
      sensor
        ? sensorActions.update(sensor.id, {
            label: label.trim() || null,
            ...(externalId.trim() !== sensor.external_id ? { external_id: externalId.trim() } : {}),
            status,
            ...(moving ? { tank_id: tankId } : {}),
          })
        : sensorActions.register({
            integration_id: chosenIntegration ?? "",
            external_id: externalId.trim(),
            label: label.trim() || undefined,
            tank_id: tankId ?? undefined,
          }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sensors"] });
      await queryClient.invalidateQueries({ queryKey: ["tanks"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={sensor ? `Edit ${sensor.label ?? sensor.external_id}` : "Register a sensor"}
        description={
          sensor
            ? `Device ${sensor.external_id}.`
            : "Its readings start with the next poll, every few minutes, or with Read now."
        }
      >
        <div className={styles.form}>
          {sensor ? null : sensorIntegrations.length === 0 && integrations.isSuccess ? (
            <Alert tone="warning" title="No sensor integration">
              Add MobiWater tank sensors in Settings › Integrations first.
            </Alert>
          ) : sensorIntegrations.length > 1 ? (
            <Select
              label="Sensor account"
              value={chosenIntegration}
              onValueChange={setIntegrationId}
              options={sensorIntegrations.map((i) => ({ value: i.id, label: i.name }))}
            />
          ) : null}
          <Input
            label="Device ID"
            description="MobiWater's Device ID, a 15-digit number (not the shorter Tank ID)"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
          />
          <Input
            label="Name (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Roof tank"
          />
          {sensor ? (
            <Select
              label="Status"
              value={status}
              onValueChange={(v) => setStatus(v as SensorStatus)}
              options={[
                { value: "active", label: "Reading" },
                { value: "inactive", label: "Off: not read" },
                { value: "faulty", label: "Faulty: not read" },
              ]}
            />
          ) : null}
          {sensor && !moving ? (
            <div className={styles.actions}>
              <span className={styles.muted}>
                {sensor.tank
                  ? `On ${[sensor.tank.label ?? "a tank", sensor.tank.location?.label].filter(Boolean).join(" at ")}.`
                  : "Not on a tank."}
              </span>
              <TextButton onClick={() => setMoving(true)}>
                {sensor.tank ? "Move or take off" : "Put on a tank"}
              </TextButton>
            </div>
          ) : (
            <>
              <TankChooser value={tankId} onChange={setTankId} />
              {sensor?.tank ? (
                <p className={styles.muted}>
                  Leave the tank empty to take the sensor off its tank. Any open low alert ends.
                </p>
              ) : null}
            </>
          )}
          {save.error ? (
            <Alert tone="danger" title={sensor ? "Not saved" : "Not registered"}>
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!externalId.trim() || (!sensor && !chosenIntegration)}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {sensor ? "Save" : "Register"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
